import { ApiError } from "@nabble/sdk";
import { AuthHeader, AuthLayout, Skeleton } from "@nabble/ui";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { confirmEmail } from "../../../api/accountEmails";
import { errorMessage } from "../../../errors";

type Result = { email: string; primary: boolean };
type State = { kind: "loading" } | { kind: "done"; result: Result } | { kind: "invalid" } | { kind: "error"; message: string };

/**
 * One request per token: the link works once, so a second call (React
 * StrictMode runs effects twice in development) would report it as invalid.
 */
const attempts = new Map<string, Promise<Result>>();

function confirmOnce(token: string): Promise<Result> {
  let attempt = attempts.get(token);
  if (!attempt) {
    attempt = confirmEmail(token);
    attempts.set(token, attempt);
  }
  return attempt;
}

/** Test hook: forget the requests already made. */
export function resetConfirmAttempts(): void {
  attempts.clear();
}

/** Public: `/account/confirm-email?token=…`, opened from the email, with or without a session. */
export default function ConfirmEmailPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>(token ? { kind: "loading" } : { kind: "invalid" });

  useEffect(() => {
    if (!token) return;
    let active = true;
    confirmOnce(token)
      .then((result) => {
        if (active) setState({ kind: "done", result });
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError && err.code === "invalid_link") setState({ kind: "invalid" });
        else setState({ kind: "error", message: errorMessage(err, "We couldn't confirm your email. Please try again.") });
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (state.kind === "loading") {
    return (
      <AuthLayout>
        <AuthHeader icon="mail" title="Confirming your email" lead="This takes just a moment." />
        <div className="nb-auth-content" aria-busy="true">
          <Skeleton height={44} radius={10} />
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === "done") {
    const { email, primary } = state.result;
    return (
      <AuthLayout>
        <AuthHeader
          icon="checkCircle"
          iconTone="success"
          title={primary ? `Your email is now ${email}` : `${email} is confirmed`}
          lead={primary ? "Use it from now on to sign in." : "You can also use it to recover your password."}
        />
        <div className="nb-auth-content">
          <Link to="/" className="nb-btn nb-btn-primary nb-btn-lg nb-btn-block">
            Go to Nabble Suite
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeader
        icon="alert"
        iconTone="warning"
        title={state.kind === "invalid" ? "This link isn't valid" : "We couldn't confirm your email"}
        lead={state.kind === "invalid" ? "This link is no longer valid. Ask for a new one from My account." : state.message}
      />
      <div className="nb-auth-content">
        <Link to="/account/profile" className="nb-btn nb-btn-primary nb-btn-lg nb-btn-block">
          Go to My account
        </Link>
      </div>
    </AuthLayout>
  );
}
